// src/components/ChapterDetailTabs.tsx (Now effectively ChapterWorkArea)

import React, { useState } from 'react';
import { Editor } from '@tiptap/react';
import type { ProjectState } from '../types/project';
import type { Chapter, ChapterDetailEdits, ChapterVersion, LoadingStates, ChapterVitals } from '../types';
import { getActiveVersion } from '../utils/chapterUtils';
import ChapterDetailsForm from './ChapterDetailsForm';
import ChapterContentEditor from './ChapterContentEditor';
import ChapterLLMSelector from './ChapterLLMSelector';
import LLMErrorBanner from './LLMErrorBanner';
import { primaryButtonClass, secondaryButtonClass, actionButtonClass } from '../constants';
import { ModelSelector } from '@/components/ModelSelector'; 

interface ChapterDetailTabsProps { // Renaming to ChapterWorkAreaProps might be good later
    projectState: ProjectState; 
    activeChapter: Chapter;
    activeChapterDetailEdits: Partial<ChapterDetailEdits> | null;
    editor: Editor | null;
    revisionInput: string;
    revisionWordCount: string;
    isLoading: LoadingStates;
    onDetailInputChange: (field: keyof Omit<ChapterDetailEdits, 'vitals'>, value: string) => void;
    onVitalsInputChange: (field: keyof ChapterVitals, value: string) => void; 
    onSaveChapterDetails: () => void;
    onGenerateChapterPreview: () => void; 
    onGenerateFullTextFromPreview: (provider?: string, model?: string) => void; 
    onGenerateFullTextForAllPreviewedChapters: () => void;
    onGenerateShortPreviewForAllChapters: (provider?: string, model?: string) => void; // ADDED for batch preview generation with model selection
    onRevisionInputChange: (value: string) => void;
    onRevisionWordCountChange: (value: string) => void;
    onReviseText: () => void;
    onNavigateVersion: (direction: 'prev' | 'next') => void;
    onSetChapterStatus: (status: Chapter['status']) => void;
    areDetailsUnsaved: boolean; 
    chapterPreviewPercentage: number; 
    onChapterPreviewPercentageChange: (percentage: number) => void; 
}

const ChapterDetailTabs: React.FC<ChapterDetailTabsProps> = ({
    projectState, activeChapter, activeChapterDetailEdits, editor, revisionInput, revisionWordCount,
    isLoading, onDetailInputChange, onVitalsInputChange, onSaveChapterDetails, 
    onGenerateChapterPreview, onGenerateFullTextFromPreview, onGenerateFullTextForAllPreviewedChapters,
    onGenerateShortPreviewForAllChapters, // ADDED
    onRevisionInputChange, onRevisionWordCountChange, onReviseText, onNavigateVersion, onSetChapterStatus,
    areDetailsUnsaved,
    chapterPreviewPercentage, onChapterPreviewPercentageChange
}) => {
    
    // LLM selection and error handling state
    const [selectedFullTextModel, setSelectedFullTextModel] = useState<{provider: string; model: string} | undefined>();
    const [selectedPreviewModel, setSelectedPreviewModel] = useState<any>(null); // For batch preview generation
    const [generationError, setGenerationError] = useState<string | null>(null);
    
    // Helper functions
    const handleFullTextGeneration = async () => {
        setGenerationError(null);
        await onGenerateFullTextFromPreview(
            selectedFullTextModel?.provider,
            selectedFullTextModel?.model
        );
    };

    const handleBatchPreviewGeneration = async () => {
        setGenerationError(null);
        await onGenerateShortPreviewForAllChapters(
            selectedPreviewModel?.provider,
            selectedPreviewModel?.apiName
        );
    };
    
    const handleModelSelect = (provider: string, model: string) => {
        if (provider === '' && model === '') {
            setSelectedFullTextModel(undefined); // Use default
        } else {
            setSelectedFullTextModel({ provider, model });
        }
    };
    
    const getCurrentModelDisplay = () => {
        if (selectedFullTextModel) {
            return `${selectedFullTextModel.provider.toUpperCase()}: ${selectedFullTextModel.model}`;
        }
        return 'Gemini 2.5 Flash (Default)';
    };
    
    const activeVersion = getActiveVersion(activeChapter);
    
    const eligibleChaptersForBatchFullText = projectState.chapters.filter(
        ch => ch.previewContent && ch.status !== 'text_generated'
    );
    const canBatchGenerateFullText = eligibleChaptersForBatchFullText.length > 0;

    const eligibleChaptersForBatchPreview = projectState.chapters.filter(
        ch => !ch.previewContent && ch.status !== 'text_generated'
    );
    const canBatchGeneratePreviews = eligibleChaptersForBatchPreview.length > 0;
    
    return (
        // <div className="h-full flex flex-col space-y-6 overflow-y-auto">
        <>
            {/* Section 1: Chapter Details & Form */}
            {activeChapter && (
                <section className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        Chapter Details
                    </h3>
                    <ChapterDetailsForm
                        detailEdits={activeChapterDetailEdits}
                        activeChapterForPreview={activeChapter} 
                        onDetailInputChange={onDetailInputChange}
                        onVitalsInputChange={onVitalsInputChange} 
                        onSaveChapterDetails={onSaveChapterDetails}
                        onGenerateChapterPreview={onGenerateChapterPreview} 
                        isLoading={isLoading}
                        areDetailsUnsaved={areDetailsUnsaved}
                        chapterPreviewPercentage={chapterPreviewPercentage} 
                        onChapterPreviewPercentageChange={onChapterPreviewPercentageChange} 
                    />
                </section>
            )}

            {/* Section 2: Content Generation */}
            <section className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                    Content Generation
                </h3>
                
                {/* Batch Preview Generation */}
                {canBatchGeneratePreviews && (
                    <div className="mb-6 p-4 border border-blue-200 bg-blue-50 rounded-xl">
                        <div className="flex items-start space-x-3">
                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-white text-sm font-bold">{eligibleChaptersForBatchPreview.length}</span>
                            </div>
                            <div className="flex-grow">
                                <p className="text-sm text-blue-800 font-medium mb-2">
                                    Generate Previews for All Chapters
                                </p>
                                <p className="text-xs text-blue-600 mb-3">
                                    Create short AI previews for {eligibleChaptersForBatchPreview.length} chapters that don't have content yet.
                                </p>

                                {/* Model Selection for Batch Preview Generation */}
                                <div className="mb-3 p-3 bg-white rounded-lg border border-blue-200">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="space-y-1">
                                            <h4 className="text-sm font-medium text-blue-900">AI Model Selection</h4>
                                            <p className="text-xs text-blue-600">
                                                Choose the AI model for preview generation
                                                {selectedPreviewModel && ` • Current: ${selectedPreviewModel.modelName}`}
                                            </p>
                                        </div>
                                        <ModelSelector
                                            taskType="chapter_text"
                                            onModelSelect={(model) => setSelectedPreviewModel(model)}
                                            baseCost={500} // Base cost for chapter preview generation
                                        >
                                            <button className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded-md hover:bg-blue-50 transition-colors">
                                                {selectedPreviewModel ? selectedPreviewModel.modelName : 'Select Model'}
                                            </button>
                                        </ModelSelector>
                                    </div>

                                    {!selectedPreviewModel && (
                                        <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                                            ⚠️ No model selected. The system will use the default model, which may be overloaded.
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={handleBatchPreviewGeneration}
                                    disabled={isLoading['genPreview'] || isLoading['genAllShortPreviews'] || isLoading['genText'] || isLoading['genAllPreviewsToFull']}
                                    className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                                >
                                    {isLoading['genAllShortPreviews'] ? (
                                        <div className="flex items-center justify-center space-x-2">
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            <span>Generating All Previews ({eligibleChaptersForBatchPreview.length})...</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-center space-x-2">
                                            <span>Generate Short Preview for All Chapters ({eligibleChaptersForBatchPreview.length})</span>
                                            {selectedPreviewModel && (
                                                <span className="text-xs opacity-80">
                                                    ({(500 * selectedPreviewModel.creditMultiplier * eligibleChaptersForBatchPreview.length).toLocaleString()} credits)
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Individual Full Text Generation */}
                {activeChapter.previewContent && activeChapter.status !== 'text_generated' && (
                    <div className="mb-6 p-4 border border-green-200 bg-green-50 rounded-xl">
                        <div className="flex items-start space-x-3">
                            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div className="flex-grow">
                                <p className="text-sm text-green-800 font-medium mb-2">
                                    Preview Ready - Generate Full Text
                                </p>
                                <p className="text-xs text-green-600 mb-3">
                                    Your chapter preview looks good. Generate the complete chapter text now.
                                </p>
                                
                                {/* Error Banner */}
                                <LLMErrorBanner 
                                    error={generationError}
                                    onDismiss={() => setGenerationError(null)}
                                    onRetryWithDifferentModel={() => {
                                        setGenerationError(null);
                                        // Could open model selector or suggest alternatives
                                    }}
                                    currentModel={getCurrentModelDisplay()}
                                />
                                
                                {/* Model Selector */}
                                <div className="mb-3">
                                    <ChapterLLMSelector
                                        taskType="chapter-full-text"
                                        onModelSelect={handleModelSelect}
                                        selectedModel={selectedFullTextModel}
                                        disabled={isLoading['genText'] || isLoading['genAllPreviewsToFull'] || isLoading['genAllShortPreviews']}
                                    />
                                </div>
                                
                                <button
                                    onClick={handleFullTextGeneration}
                                    disabled={isLoading['genText'] || isLoading['genAllPreviewsToFull'] || isLoading['genAllShortPreviews']}
                                    className="w-full py-3 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium mb-3"
                                >
                                    {isLoading['genText'] ? (
                                        <div className="flex items-center justify-center space-x-2">
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            <span>Generating Full Text...</span>
                                        </div>
                                    ) : (
                                        'Accept Preview & Generate Full Text'
                                    )}
                                </button>
                                
                                {/* Batch Full Text Generation */}
                                {canBatchGenerateFullText && (
                                    <div className="pt-3 border-t border-green-200">
                                        <p className="text-xs text-green-600 mb-2">
                                            Or generate full text for all {eligibleChaptersForBatchFullText.length} chapters with previews
                                        </p>
                                        <button
                                            onClick={onGenerateFullTextForAllPreviewedChapters}
                                            disabled={isLoading['genText'] || isLoading['genAllPreviewsToFull'] || isLoading['genAllShortPreviews']}
                                            className="w-full py-2.5 px-4 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                                        >
                                            {isLoading['genAllPreviewsToFull'] ? (
                                                <div className="flex items-center justify-center space-x-2">
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    <span>Generating All ({eligibleChaptersForBatchFullText.length})...</span>
                                                </div>
                                            ) : (
                                                `Generate Full Text for All Chapters with Previews (${eligibleChaptersForBatchFullText.length})`
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </section>

            {/* Section 2.5: Preview Content Display */}
            {activeChapter.previewContent && (
                <section className="bg-blue-50 rounded-xl border border-blue-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        Chapter Preview ({activeChapter.previewWordCountTarget || 'Generated'} words)
                    </h3>
                    
                    {activeChapter.previewSummary && (
                        <div className="mb-4 p-3 bg-white rounded-lg border border-blue-100">
                            <h4 className="font-medium text-blue-800 mb-2">AI Summary:</h4>
                            <p className="text-sm text-gray-700">{activeChapter.previewSummary}</p>
                        </div>
                    )}
                    
                    <div className="bg-white rounded-lg border border-blue-100 p-4 max-h-96 overflow-y-auto">
                        <h4 className="font-medium text-blue-800 mb-3">Preview Content:</h4>
                        <div 
                            className="prose prose-sm max-w-none text-gray-700" 
                            dangerouslySetInnerHTML={{ __html: activeChapter.previewContent }}
                        />
                    </div>
                    
                    <div className="mt-4 text-xs text-blue-600 bg-blue-100 p-2 rounded">
                        💡 This is a preview of your chapter. Generate full text above to expand it into the complete chapter.
                    </div>
                </section>
            )}

            {/* Section 3: Content Editor & Revisions */}
            {activeChapter.status === 'text_generated' && editor && (
                <section className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                        Content & Revisions
                    </h3>
                    <ChapterContentEditor
                        activeChapter={activeChapter}
                        activeVersion={activeVersion || { content: '', generatedSummary: null, timestamp: Date.now(), previewText: '' }}
                        editor={editor}
                        revisionInput={revisionInput}
                        onRevisionInputChange={onRevisionInputChange}
                        revisionWordCount={revisionWordCount}
                        onRevisionWordCountChange={onRevisionWordCountChange}
                        onReviseText={onReviseText}
                        onNavigateVersion={onNavigateVersion}
                        onSetChapterStatus={onSetChapterStatus}
                        isLoading={isLoading}
                    />
                </section>
            )}

            {/* Placeholder if no preview and no content */}
            {activeChapter.status !== 'text_generated' && !activeChapter.previewContent && !canBatchGeneratePreviews && (
                <section className="bg-gray-50 rounded-xl p-8 text-center">
                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                    </div>
                    <h4 className="text-lg font-medium text-gray-800 mb-2">Ready to Create Content</h4>
                    <p className="text-gray-600 max-w-md mx-auto">
                        Generate a short preview for this chapter first using the chapter details section above, or use the batch operation if available.
                    </p>
                </section>
            )}
        </>
    );
};

export default ChapterDetailTabs;